import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
	ArrowLeft,
	Building2,
	User,
	Lock,
	Mail,
	Package,
	ChevronDown,
	ChevronUp,
	Eye,
	EyeOff,
	CheckCircle,
	DollarSign,
	Calendar,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/utils/cn";
import * as api from "@/services/api";

const plans = [
	{
		value: "free",
		label: "Free",
		description: "Basic features for small teams",
	},
	{
		value: "starter",
		label: "Starter",
		description: "Essential features for growing teams",
	},
	{
		value: "professional",
		label: "Professional",
		description: "Advanced features for larger organizations",
	},
	{
		value: "enterprise",
		label: "Enterprise",
		description: "Full access with premium support",
	},
] as const;

function generateSlug(name: string): string {
	return name
		.toLowerCase()
		.replace(/[^a-z0-9\s-]/g, "")
		.replace(/\s+/g, "-")
		.replace(/-+/g, "-")
		.trim();
}

export function OrganizationCreate() {
	const navigate = useNavigate();
	const { toast } = useToast();

	// Form state
	const [organizationName, setOrganizationName] = useState("");
	const [slug, setSlug] = useState("");
	const [slugTouched, setSlugTouched] = useState(false);
	const [plan, setPlan] = useState<
		"free" | "starter" | "professional" | "enterprise"
	>("free");

	// Owner state
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	// Purchased seats state
	const [purchasedSeats, setPurchasedSeats] = useState("");

	// Custom package state
	const [showCustomPackage, setShowCustomPackage] = useState(false);
	const [customPackageEnabled, setCustomPackageEnabled] = useState(false);
	const [customFeatures, setCustomFeatures] = useState("");
	const [blockedFeatures, setBlockedFeatures] = useState("");
	const [customNotes, setCustomNotes] = useState("");

	// Custom pricing state
	const [enableCustomPricing, setEnableCustomPricing] = useState(false);
	const [customPrice, setCustomPrice] = useState("");
	const [customCurrency, setCustomCurrency] = useState("USD");
	const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">(
		"monthly",
	);
	const [customTerms, setCustomTerms] = useState("");
	const [effectiveDate, setEffectiveDate] = useState("");

	// Fetch currencies from API
	const { data: currencies = [] } = useQuery({
		queryKey: ["admin-currencies"],
		queryFn: () => api.getCurrencies(),
	});

	// Auto-generate slug from organization name
	useEffect(() => {
		if (!slugTouched && organizationName) {
			setSlug(generateSlug(organizationName));
		}
	}, [organizationName, slugTouched]);

	const createMutation = useMutation({
		mutationFn: (data: api.CreateOrganizationPayload) =>
			api.createAdminOrganization(data),
		onSuccess: (data) => {
			toast({
				title: "Organization created",
				description: `${data.organization.name} has been created successfully with owner ${data.owner.email}`,
			});
			navigate(`/organizations/${data.organization._id}`);
		},
		onError: (error: Error) => {
			toast({
				title: "Error creating organization",
				description: error.message,
				variant: "destructive",
			});
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		// Validation
		if (!organizationName.trim()) {
			toast({
				title: "Validation Error",
				description: "Organization name is required",
				variant: "destructive",
			});
			return;
		}

		if (!firstName.trim()) {
			toast({
				title: "Validation Error",
				description: "Owner first name is required",
				variant: "destructive",
			});
			return;
		}

		if (!email.trim()) {
			toast({
				title: "Validation Error",
				description: "Owner email is required",
				variant: "destructive",
			});
			return;
		}

		if (!password || password.length < 6) {
			toast({
				title: "Validation Error",
				description: "Password must be at least 6 characters",
				variant: "destructive",
			});
			return;
		}

		if (password !== confirmPassword) {
			toast({
				title: "Validation Error",
				description: "Passwords do not match",
				variant: "destructive",
			});
			return;
		}

		const payload: api.CreateOrganizationPayload = {
			organizationName: organizationName.trim(),
			slug: slug.trim() || undefined,
			plan,
			purchasedSeats: purchasedSeats ? parseInt(purchasedSeats, 10) : undefined,
			owner: {
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				email: email.trim().toLowerCase(),
				password,
				confirmPassword,
			},
		};

		if (customPackageEnabled) {
			payload.customPackage = {
				enabled: true,
				customFeatures: customFeatures
					.split(",")
					.map((f) => f.trim())
					.filter(Boolean),
				blockedFeatures: blockedFeatures
					.split(",")
					.map((f) => f.trim())
					.filter(Boolean),
				notes: customNotes.trim(),
				effectiveDate: effectiveDate || undefined,
			};

			// Add custom pricing if enabled
			if (enableCustomPricing && customPrice) {
				payload.customPackage.customPricing = {
					price: parseFloat(customPrice),
					currency: customCurrency,
					billingPeriod,
					customTerms: customTerms.trim() || undefined,
				};
			}
		}

		createMutation.mutate(payload);
	};

	const passwordsMatch = password && confirmPassword && password === confirmPassword;
	const passwordValid = password.length >= 6;

	return (
		<div className="space-y-6 max-w-4xl mx-auto">
			{/* Header */}
			<div className="flex items-center gap-4">
				<Button
					variant="ghost"
					size="sm"
					onClick={() => navigate("/organizations")}
				>
					<ArrowLeft className="w-4 h-4 mr-2" />
					Back to Organizations
				</Button>
			</div>

			<div>
				<h1 className="text-2xl font-bold text-gray-900">New Organization</h1>
				<p className="text-gray-500 mt-1">
					Create a new organization with an owner account. This bypasses Stripe
					and sets the subscription as active immediately.
				</p>
			</div>

			<form onSubmit={handleSubmit}>
				{/* Organization Details */}
				<Card className="mb-6">
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<Building2 className="w-5 h-5" />
							Organization Details
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Organization Name <span className="text-red-500">*</span>
								</label>
								<Input
									value={organizationName}
									onChange={(e) => setOrganizationName(e.target.value)}
									placeholder="Acme Corporation"
									required
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Slug
								</label>
								<Input
									value={slug}
									onChange={(e) => {
										setSlugTouched(true);
										setSlug(e.target.value);
									}}
									placeholder="acme-corporation"
								/>
								<p className="text-xs text-gray-500 mt-1">
									Auto-generated from name. Leave empty for auto-generation.
								</p>
							</div>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Subscription Plan <span className="text-red-500">*</span>
							</label>
							<Select
								value={plan}
								onValueChange={(value) =>
									setPlan(
										value as "free" | "starter" | "professional" | "enterprise",
									)
								}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select a plan" />
								</SelectTrigger>
								<SelectContent>
									{plans.map((p) => (
										<SelectItem key={p.value} value={p.value}>
											<div className="flex items-center gap-2">
												<span className="font-medium">{p.label}</span>
												<span className="text-gray-500 text-xs">
													- {p.description}
												</span>
											</div>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<div className="mt-2 flex items-center gap-2">
								<Badge
									className={cn(
										plan === "free" && "bg-gray-100 text-gray-700",
										plan === "starter" && "bg-blue-100 text-blue-700",
										plan === "professional" && "bg-purple-100 text-purple-700",
										plan === "enterprise" && "bg-amber-100 text-amber-700",
									)}
								>
									{plan.charAt(0).toUpperCase() + plan.slice(1)}
								</Badge>
								<Badge className="bg-green-100 text-green-700">
									<CheckCircle className="w-3 h-3 mr-1" />
									Will be Active
								</Badge>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Owner Details */}
				<Card className="mb-6">
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<User className="w-5 h-5" />
							Owner Account
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									First Name <span className="text-red-500">*</span>
								</label>
								<Input
									value={firstName}
									onChange={(e) => setFirstName(e.target.value)}
									placeholder="John"
									required
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Last Name
								</label>
								<Input
									value={lastName}
									onChange={(e) => setLastName(e.target.value)}
									placeholder="Doe"
								/>
							</div>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								<Mail className="w-4 h-4 inline mr-1" />
								Email <span className="text-red-500">*</span>
							</label>
							<Input
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="john@acme.com"
								required
							/>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									<Lock className="w-4 h-4 inline mr-1" />
									Password <span className="text-red-500">*</span>
								</label>
								<div className="relative">
									<Input
										type={showPassword ? "text" : "password"}
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										placeholder="••••••••"
										required
										minLength={6}
									/>
									<button
										type="button"
										onClick={() => setShowPassword(!showPassword)}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
									>
										{showPassword ? (
											<EyeOff className="w-4 h-4" />
										) : (
											<Eye className="w-4 h-4" />
										)}
									</button>
								</div>
								{password && !passwordValid && (
									<p className="text-xs text-red-500 mt-1">
										Minimum 6 characters required
									</p>
								)}
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Confirm Password <span className="text-red-500">*</span>
								</label>
								<div className="relative">
									<Input
										type={showConfirmPassword ? "text" : "password"}
										value={confirmPassword}
										onChange={(e) => setConfirmPassword(e.target.value)}
										placeholder="••••••••"
										required
									/>
									<button
										type="button"
										onClick={() => setShowConfirmPassword(!showConfirmPassword)}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
									>
										{showConfirmPassword ? (
											<EyeOff className="w-4 h-4" />
										) : (
											<Eye className="w-4 h-4" />
										)}
									</button>
								</div>
								{confirmPassword && !passwordsMatch && (
									<p className="text-xs text-red-500 mt-1">
										Passwords do not match
									</p>
								)}
								{passwordsMatch && (
									<p className="text-xs text-green-600 mt-1 flex items-center gap-1">
										<CheckCircle className="w-3 h-3" />
										Passwords match
									</p>
								)}
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Custom Package (Collapsible) */}
				<Card className="mb-6">
					<CardHeader
						className="cursor-pointer"
						onClick={() => setShowCustomPackage(!showCustomPackage)}
					>
						<CardTitle className="flex items-center justify-between">
							<div className="flex items-center gap-2">
								<Package className="w-5 h-5" />
								Custom Package
								<Badge variant="outline" className="ml-2 font-normal">
									Optional
								</Badge>
							</div>
							{showCustomPackage ? (
								<ChevronUp className="w-5 h-5 text-gray-400" />
							) : (
								<ChevronDown className="w-5 h-5 text-gray-400" />
							)}
						</CardTitle>
					</CardHeader>
					{showCustomPackage && (
						<CardContent className="space-y-4">
							{/* Purchased Seats */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Purchased Seats
								</label>
								<Input
									type="number"
									min="0"
									value={purchasedSeats}
									onChange={(e) => setPurchasedSeats(e.target.value)}
									placeholder="e.g. 10"
									className="w-48"
								/>
								<p className="text-xs text-gray-500 mt-1">
									For non-custom orgs, purchased seats are set automatically.
								</p>
							</div>

							<div className="flex items-center gap-3">
								<input
									type="checkbox"
									id="customPackageEnabled"
									checked={customPackageEnabled}
									onChange={(e) => setCustomPackageEnabled(e.target.checked)}
									className="rounded border-gray-300 w-4 h-4"
								/>
								<label htmlFor="customPackageEnabled" className="text-sm">
									Enable custom package for this organization
								</label>
							</div>

							{customPackageEnabled && (
								<>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Custom Features (comma-separated feature codes)
										</label>
										<Input
											value={customFeatures}
											onChange={(e) => setCustomFeatures(e.target.value)}
											placeholder="feature1, feature2, feature3"
										/>
										<p className="text-xs text-gray-500 mt-1">
											Features to add beyond the base plan
										</p>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Blocked Features (comma-separated feature codes)
										</label>
										<Input
											value={blockedFeatures}
											onChange={(e) => setBlockedFeatures(e.target.value)}
											placeholder="feature1, feature2"
										/>
										<p className="text-xs text-gray-500 mt-1">
											Features to remove from the base plan
										</p>
									</div>

									{/* Effective Date */}
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											<Calendar className="w-4 h-4 inline mr-1" />
											Effective Date
										</label>
										<Input
											type="date"
											value={effectiveDate}
											onChange={(e) => setEffectiveDate(e.target.value)}
										/>
										<p className="text-xs text-gray-500 mt-1">
											When this custom package takes effect (defaults to now)
										</p>
									</div>

									{/* Custom Pricing Section */}
									<div className="border-t pt-4 mt-4">
										<div className="flex items-center gap-3 mb-4">
											<input
												type="checkbox"
												id="enableCustomPricing"
												checked={enableCustomPricing}
												onChange={(e) =>
													setEnableCustomPricing(e.target.checked)
												}
												className="rounded border-gray-300 w-4 h-4"
											/>
											<label
												htmlFor="enableCustomPricing"
												className="text-sm font-medium flex items-center gap-1"
											>
												<DollarSign className="w-4 h-4" />
												Enable Custom Pricing
											</label>
										</div>

										{enableCustomPricing && (
											<div className="space-y-4 pl-7">
												<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1">
															Price
														</label>
														<Input
															type="number"
															step="0.01"
															min="0"
															value={customPrice}
															onChange={(e) => setCustomPrice(e.target.value)}
															placeholder="99.99"
														/>
													</div>
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1">
															Currency
														</label>
														<Select
															value={customCurrency}
															onValueChange={setCustomCurrency}
														>
															<SelectTrigger>
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																{currencies.map((c) => (
																	<SelectItem key={c._id} value={c.code}>
																		{c.code} ({c.symbol})
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
													</div>
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1">
															Billing Period
														</label>
														<Select
															value={billingPeriod}
															onValueChange={(value) =>
																setBillingPeriod(value as "monthly" | "yearly")
															}
														>
															<SelectTrigger>
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																<SelectItem value="monthly">Monthly</SelectItem>
																<SelectItem value="yearly">Yearly</SelectItem>
															</SelectContent>
														</Select>
													</div>
												</div>

												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">
														Custom Terms
													</label>
													<textarea
														value={customTerms}
														onChange={(e) => setCustomTerms(e.target.value)}
														className="w-full px-3 py-2 border rounded-md text-sm resize-none"
														rows={2}
														placeholder="Special pricing terms, discounts, agreements..."
													/>
												</div>
											</div>
										)}
									</div>

									{/* Notes */}
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Notes
										</label>
										<textarea
											value={customNotes}
											onChange={(e) => setCustomNotes(e.target.value)}
											className="w-full px-3 py-2 border rounded-md text-sm resize-none"
											rows={3}
											placeholder="Internal notes about this custom package..."
										/>
									</div>
								</>
							)}
						</CardContent>
					)}
				</Card>

				{/* Actions */}
				<div className="flex justify-end gap-3">
					<Button
						type="button"
						variant="outline"
						onClick={() => navigate("/organizations")}
					>
						Cancel
					</Button>
					<Button
						type="submit"
						disabled={createMutation.isPending}
						className="bg-blue-600 hover:bg-blue-700"
					>
						{createMutation.isPending ? (
							<>
								<div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
								Creating...
							</>
						) : (
							<>
								<Building2 className="w-4 h-4 mr-2" />
								Create Organization
							</>
						)}
					</Button>
				</div>
			</form>
		</div>
	);
}
